import { createSignal, createEffect, onCleanup, untrack, For, Show } from 'solid-js';
import { useNavigate } from '@solidjs/router';
import Text from '../components/Text';
import SellbotNav from '../components/SellbotNav';
import ItemStackView from '../components/ItemStackView';
import TextWithCopyButton from '../components/TextWithCopyButton';
import { Badge, type BadgeTone } from '../components/Badge';
import { EmptyState } from '../components/EmptyState';
import { SkeletonCard } from '../components/Skeleton';
import { showConfirm } from '../components/Dialog';
import { useToast } from '../components/MessageBox';
import { useApi, isOk } from '../api';
import { firstError } from '../api/envelope';
import { useActiveBot } from '../state/activeBot';
import { useTranslation } from '../hooks/useTranslation';
import { formatDateTime } from '../services/dateFormat';
import { fromItemStackDto } from '../services/itemRefAdapters';
import { subscribeBotEngineEvents } from '../services/botEngineEvents';
import type { SellbotGrantDto, SellbotOfferDto } from '../api';
import sellbotStyles from './Sellbot.module.css';
import cardStyles from './SellbotHolograms.module.css';

const STATE_TONES: Record<string, BadgeTone> = {
    Active: 'success',
    Revoked: 'error',
    Expired: 'warning',
    UsedUp: 'neutral',
};

export default function SellbotGrants() {
    const api = useApi();
    const navigate = useNavigate();
    const toast = useToast();
    const { t } = useTranslation();
    const { botId } = useActiveBot();

    const [loading, setLoading] = createSignal(true);
    const [grants, setGrants] = createSignal<SellbotGrantDto[]>([]);
    const [offers, setOffers] = createSignal<SellbotOfferDto[]>([]);

    const loadAll = async () => {
        const id = botId();
        if (!id) { setGrants([]); setLoading(false); return; }

        const [list, cfg] = await Promise.all([api.sellbotGrant.list(id), api.sellbot.getConfig(id)]);
        if (isOk(list.data)) setGrants(list.data.value);
        else toast?.error(firstError(list.data) ?? t('SELLBOT_GRANTS_LOAD_FAILED'));
        if (isOk(cfg.data)) setOffers(cfg.data.value.offers);
        setLoading(false);
    };

    createEffect(() => {
        const id = botId();
        setLoading(true);
        untrack(() => { void loadAll(); });
        if (!id) return;
        const unsub = subscribeBotEngineEvents(id, (evt) => {
            if (evt.type === 'plugin_event' && evt.payload?.source === 'sellbot') void loadAll();
        });
        onCleanup(unsub);
    });

    const offerOf = (offerId: string) => offers().find(offer => offer.id === offerId);

    const revoke = async (grant: SellbotGrantDto) => {
        const id = botId();
        if (!id) return;
        if (!await showConfirm(t('SELLBOT_GRANTS_CONFIRM_REVOKE', grant.code), true)) return;
        const res = await api.sellbotGrant.revoke(id, grant.id);
        if (isOk(res.data)) { toast?.success(t('SELLBOT_GRANTS_TOAST_REVOKED')); void loadAll(); }
        else toast?.error(firstError(res.data) ?? t('SELLBOT_GRANTS_TOAST_REVOKE_FAILED'));
    };

    const remove = async (grant: SellbotGrantDto) => {
        const id = botId();
        if (!id) return;
        if (!await showConfirm(t('SELLBOT_GRANTS_CONFIRM_DELETE', grant.code), true)) return;
        const res = await api.sellbotGrant.delete(id, grant.id);
        if (isOk(res.data)) { toast?.success(t('SELLBOT_GRANTS_TOAST_DELETED')); void loadAll(); }
        else toast?.error(firstError(res.data) ?? t('SELLBOT_GRANTS_TOAST_DELETE_FAILED'));
    };

    return (
        <div class={sellbotStyles.container}>
            <SellbotNav />

            <div class={sellbotStyles.headerRow}>
                <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                    <h1><Text val="SELLBOT_GRANTS_TITLE" /></h1>
                    <span class={sellbotStyles.heroSubtitle}><Text val="SELLBOT_GRANTS_SUBTITLE" /></span>
                </div>
                <button class="btn btn-primary" onClick={() => navigate('/sellbot/grants/new')} disabled={!botId()}>
                    <i class="fa-solid fa-plus" />
                    <Text val="SELLBOT_GRANTS_NEW" />
                </button>
            </div>

            <Show when={!loading()} fallback={<div class="v" style={{ gap: 'var(--spacing-md)' }}><SkeletonCard /><SkeletonCard /></div>}>
                <Show
                    when={grants().length > 0}
                    fallback={<EmptyState icon="fa-ticket" title={t('SELLBOT_GRANTS_EMPTY_TITLE')} hint={t('SELLBOT_GRANTS_EMPTY_HINT')} />}
                >
                    <div class={cardStyles.grid}>
                        <For each={grants()}>
                            {(grant) => (
                                <div class={`fcard v ${cardStyles.card}`}>
                                    <div class="h hcenter" style={{ 'justify-content': 'space-between', gap: 'var(--spacing-sm)' }}>
                                        <strong class={cardStyles.cardTitle}>{grant.label}</strong>
                                        <Badge tone={STATE_TONES[grant.state] ?? 'neutral'}>
                                            <Text val={'SELLBOT_GRANTS_STATE_' + grant.state.toUpperCase()} />
                                        </Badge>
                                    </div>

                                    <TextWithCopyButton text={grant.code} />

                                    <div class="h" style={{ gap: 'var(--spacing-sm)', 'flex-wrap': 'wrap' }}>
                                        <For each={grant.lines}>
                                            {(line) => (
                                                <Show when={offerOf(line.sellbotOfferId)}
                                                    fallback={<span class={cardStyles.cardMeta}><Text val="SELLBOT_GRANTS_UNKNOWN_ITEM" /></span>}>
                                                    {(offer) => <ItemStackView stack={{ ...fromItemStackDto(offer().item), count: line.itemCount }} size={32} />}
                                                </Show>
                                            )}
                                        </For>
                                    </div>

                                    <span class={cardStyles.cardMeta}>
                                        {t('SELLBOT_GRANTS_USES', String(grant.uses), String(grant.maxRedemptions))}
                                        {' · '}
                                        {grant.boundPlayerName ? t('SELLBOT_GRANTS_BOUND_TO', grant.boundPlayerName) : t('SELLBOT_GRANTS_ANY_PLAYER')}
                                        <Show when={grant.expiresAt}> · {t('SELLBOT_GRANTS_EXPIRES', formatDateTime(grant.expiresAt!))}</Show>
                                    </span>

                                    <div class="h" style={{ 'justify-content': 'flex-end', gap: 'var(--spacing-xs)' }}>
                                        <button class="btn btn-secondary" onClick={() => navigate(`/sellbot/grants/${grant.id}`)}>
                                            <i class="fa-solid fa-pen" />
                                            <Text val="SELLBOT_GRANTS_EDIT" />
                                        </button>
                                        <Show when={!grant.revokedAt}>
                                            <button class="btn btn-secondary" onClick={() => void revoke(grant)}>
                                                <i class="fa-solid fa-ban" />
                                                <Text val="SELLBOT_GRANTS_REVOKE" />
                                            </button>
                                        </Show>
                                        <button class="btn btn-icon btn-danger" title={t('SELLBOT_GRANTS_DELETE')} onClick={() => void remove(grant)}>
                                            <i class="fa-solid fa-trash" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </For>
                    </div>
                </Show>
            </Show>
        </div>
    );
}
